import { useEffect, useState } from 'react';
import { ApiError, api } from '../api/client';
import type { AdminClaimRequest, ProposedDevice } from '../api/types';
import { MODELS } from '../models';
import { IconAlert, IconCheck, IconClose, IconPencil, IconPlus } from '../components/Icons';
import { useToast } from '../components/Toast';
import { formatAgo, formatDateTime, useT } from '../i18n';

type Filter = 'pending' | 'approved' | 'rejected' | 'all';

/** What the device row would be created from, before the admin touches it. */
function proposalOf(row: AdminClaimRequest): ProposedDevice {
  return {
    name: row.proposed_name ?? '',
    model: row.proposed_model ?? MODELS[0],
    plate: row.proposed_plate ?? '',
    simNumber: row.proposed_sim ?? '',
    hasImmobiliser: row.proposed_has_immobiliser ?? true,
  };
}

/**
 * The review queue.
 *
 * With no activation code in the flow, approving here is the entire security
 * boundary — it hands over live tracking and, on a fitted device, the
 * immobiliser. So every row carries what the decision rests on rather than
 * making the administrator go and look it up.
 */
export function ClaimRequests({ onChanged }: { onChanged: () => void }) {
  const { t, lang } = useT();
  const toast = useToast();

  const [requests, setRequests] = useState<AdminClaimRequest[]>([]);
  const [filter, setFilter] = useState<Filter>('pending');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState<AdminClaimRequest | null>(null);
  const [reason, setReason] = useState('');

  /**
   * Edits keyed by request, so several rows can be corrected at once — and so a
   * request that arrived before the customer form existed, carrying no details
   * at all, can simply be filled in here.
   */
  const [drafts, setDrafts] = useState<Record<number, ProposedDevice>>({});
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const draftFor = (row: AdminClaimRequest): ProposedDevice => drafts[row.id] ?? proposalOf(row);

  const setDraft = (row: AdminClaimRequest, patch: Partial<ProposedDevice>) =>
    setDrafts((current) => ({ ...current, [row.id]: { ...draftFor(row), ...patch } }));

  const reload = async (which: Filter = filter) => {
    setLoading(true);
    try {
      const rows = await api.admin.claimRequests(which === 'all' ? undefined : which);
      setRequests(rows);

      // Open the editor straight away where there is nothing to approve with:
      // otherwise the admin is looking at a dead button and no way to fix it.
      setExpanded(
        new Set(
          rows
            .filter((r) => r.status === 'pending' && r.device_id === null && !r.proposed_name)
            .map((r) => r.id),
        ),
      );
    } catch (cause) {
      toast('err', (cause as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const approve = async (row: AdminClaimRequest) => {
    const unknownDevice = row.device_id === null;
    const device = draftFor(row);

    if (unknownDevice && !device.name.trim()) {
      // Nothing to name the vehicle: open the editor rather than fail silently.
      setExpanded((current) => new Set(current).add(row.id));
      toast('err', t('needVehicleName'));
      return;
    }

    // Taking a vehicle off its current owner is not something to do by reflex.
    if (row.owner_id && row.owner_id !== row.user_id) {
      const ok = confirm(
        `${t('confirmTransfer')}\n\n${row.owner_name} → ${row.user_name}\n${row.device_name ?? row.imei}`,
      );
      if (!ok) return;
    }

    setBusyId(row.id);
    try {
      await api.admin.approveClaim(row.id, unknownDevice ? device : undefined);
      toast('ok', t('claimApproved'));
      await reload();
      onChanged();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    setBusyId(rejecting.id);
    try {
      await api.admin.rejectClaim(rejecting.id, reason.trim() || undefined);
      toast('ok', t('claimRejected'));
      setRejecting(null);
      setReason('');
      await reload();
    } catch (cause) {
      toast('err', cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setBusyId(null);
    }
  };

  const filters: Array<{ key: Filter; label: string }> = [
    { key: 'pending', label: t('statusPending') },
    { key: 'approved', label: t('statusApproved') },
    { key: 'rejected', label: t('statusRejected') },
    { key: 'all', label: t('filterAll') },
  ];

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h3>{t('claimRequests')}</h3>
        <span className="count">{requests.length}</span>
        <div className="preset-row" style={{ marginInlineStart: 'auto' }}>
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              className={filter === f.key ? 'chip active' : 'chip'}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton" style={{ height: 84 }} />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <p className="muted small">{t('noRequests')}</p>
      ) : (
        <ul className="claim-list">
          {requests.map((row) => {
            const unknownDevice = row.device_id === null;
            const takenFromSomeoneElse = Boolean(row.owner_id && row.owner_id !== row.user_id);
            const draft = draftFor(row);
            const editing = expanded.has(row.id);
            const canApprove = !unknownDevice || draft.name.trim().length > 0;

            return (
              <li key={row.id} className={`claim-card ${row.status}`}>
                <div className="claim-head">
                  <div>
                    <strong>{row.user_name}</strong>
                    {row.user_self_registered && (
                      <span className="tag" style={{ marginInlineStart: 6 }}>
                        {t('selfRegistered')}
                      </span>
                    )}
                    <div className="muted tiny" dir="ltr">
                      {row.user_email}
                      {row.user_phone ? ` · ${row.user_phone}` : ''}
                    </div>
                  </div>
                  <span className={`pill ${row.status === 'pending' ? 'offline' : 'online'}`}>
                    {row.status === 'pending'
                      ? t('statusPending')
                      : row.status === 'approved'
                        ? t('statusApproved')
                        : t('statusRejected')}
                  </span>
                </div>

                <dl className="claim-facts">
                  <div>
                    <dt>{t('imei')}</dt>
                    <dd dir="ltr">{row.imei}</dd>
                  </div>
                  <div>
                    <dt>{t('vehicles')}</dt>
                    <dd>
                      {unknownDevice ? (
                        <span className="warn-text">{t('imeiNotRegistered')}</span>
                      ) : (
                        `${row.device_name}${row.device_plate ? ` · ${row.device_plate}` : ''}`
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>{t('owner')}</dt>
                    <dd>
                      {row.owner_name ? (
                        takenFromSomeoneElse ? (
                          <span className="danger-text">{row.owner_name}</span>
                        ) : (
                          row.owner_name
                        )
                      ) : (
                        <span className="muted">{t('unclaimed')}</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>{t('lastUpdate')}</dt>
                    <dd>{formatAgo(row.created_at, lang)}</dd>
                  </div>
                </dl>

                {row.note && <p className="claim-note">“{row.note}”</p>}

                {takenFromSomeoneElse && row.status === 'pending' && (
                  <p
                    className="form-error"
                    style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                  >
                    <IconAlert />
                    {t('warnTransfer')}
                  </p>
                )}

                {unknownDevice && row.status === 'pending' && (
                  <div className="proposal">
                    <div className="proposal-head">
                      <span className="code-head">
                        {row.proposed_name ? t('proposedDetails') : t('fillVehicleDetails')}
                      </span>
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() =>
                          setExpanded((current) => {
                            const next = new Set(current);
                            if (next.has(row.id)) next.delete(row.id);
                            else next.add(row.id);
                            return next;
                          })
                        }
                      >
                        <IconPencil size={13} />{' '}
                        {editing ? t('close') : t('editBeforeAdding')}
                      </button>
                    </div>

                    {editing ? (
                      <div className="proposal-form">
                        <label>
                          {t('vehicleName')}
                          <input
                            value={draft.name}
                            onChange={(e) => setDraft(row, { name: e.target.value })}
                            placeholder={t('vehicleNamePlaceholder')}
                            required
                          />
                        </label>
                        <label>
                          {t('model')}
                          <select
                            value={draft.model}
                            onChange={(e) => setDraft(row, { model: e.target.value })}
                          >
                            {MODELS.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          {t('plate')}
                          <input
                            value={draft.plate ?? ''}
                            onChange={(e) => setDraft(row, { plate: e.target.value })}
                          />
                        </label>
                        <label>
                          {t('simNumber')}
                          <input
                            dir="ltr"
                            value={draft.simNumber ?? ''}
                            onChange={(e) => setDraft(row, { simNumber: e.target.value })}
                          />
                        </label>
                        <label className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={draft.hasImmobiliser ?? true}
                            onChange={(e) => setDraft(row, { hasImmobiliser: e.target.checked })}
                          />
                          {t('hasImmobiliser')}
                        </label>
                      </div>
                    ) : (
                      <dl className="claim-facts">
                        <div>
                          <dt>{t('vehicleName')}</dt>
                          <dd>{draft.name || '—'}</dd>
                        </div>
                        <div>
                          <dt>{t('model')}</dt>
                          <dd>{draft.model}</dd>
                        </div>
                        <div>
                          <dt>{t('plate')}</dt>
                          <dd>{draft.plate || '—'}</dd>
                        </div>
                        <div>
                          <dt>{t('simNumber')}</dt>
                          <dd dir="ltr">{draft.simNumber || '—'}</dd>
                        </div>
                      </dl>
                    )}

                    <p className="muted tiny" style={{ margin: 0 }}>
                      {canApprove ? t('notRegisteredYet') : t('needVehicleName')}
                    </p>
                  </div>
                )}

                {row.status === 'pending' ? (
                  <div className="btn-grid">
                    <button
                      type="button"
                      className="btn success"
                      disabled={busyId !== null || !canApprove}
                      onClick={() => approve(row)}
                    >
                      {busyId === row.id ? (
                        <span className="btn-spinner" />
                      ) : unknownDevice ? (
                        <IconPlus />
                      ) : (
                        <IconCheck />
                      )}
                      {unknownDevice ? t('addDeviceAndApprove') : t('approve')}
                    </button>
                    <button
                      type="button"
                      className="btn danger"
                      disabled={busyId !== null}
                      onClick={() => {
                        setRejecting(row);
                        setReason('');
                      }}
                    >
                      <IconClose />
                      {t('reject')}
                    </button>
                  </div>
                ) : (
                  <p className="muted tiny">
                    {row.decided_at ? formatDateTime(row.decided_at, lang) : ''}
                    {row.reason ? ` — ${row.reason}` : ''}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {rejecting && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>{t('reject')}</h3>
            <p className="muted small" style={{ margin: 0 }}>
              {rejecting.user_name} — <span dir="ltr">{rejecting.imei}</span>
            </p>
            <label>
              {t('rejectReason')}
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={300}
                placeholder={t('rejectReasonPlaceholder')}
                autoFocus
              />
            </label>
            <span className="muted tiny">{t('rejectReasonHint')}</span>
            <div className="btn-grid">
              <button type="button" className="btn danger" onClick={reject}>
                {t('reject')}
              </button>
              <button type="button" className="btn" onClick={() => setRejecting(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
